"use client";

import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@punchup/ui-common/shadcn/ui/popover";
import { ShowWithComedian } from "@punchup/data/legacy";
import { VenuePageEvents, trackEvent } from "@punchup/analytics";
import ShowsModalOrDrawer from "./ShowsModalOrDrawer";
import { cn } from "@punchup/utils/cn";
import dayjs from "@punchup/utils/dayjs";
import { lightenColor } from "@punchup/utils/colors";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVenueCalendarShows } from "../../hooks";

interface CalendarDay {
  date: dayjs.Dayjs;
  shows: ShowWithComedian[];
  isCurrentMonth: boolean;
  isToday: boolean;
}

/**
 * Generate calendar grid for a single month with shows placed on their days
 */
function generateCalendarForMonth(
  month: dayjs.Dayjs,
  shows: ShowWithComedian[]
): CalendarDay[] {
  const today = dayjs();
  const startOfMonth = month.startOf("month");
  const endOfMonth = month.endOf("month");
  const startOfCalendar = startOfMonth.startOf("week");
  const endOfCalendar = endOfMonth.endOf("week");

  const days: CalendarDay[] = [];
  let currentDay = startOfCalendar;

  while (
    currentDay.isBefore(endOfCalendar, "day") ||
    currentDay.isSame(endOfCalendar, "day")
  ) {
    const dayShows = shows.filter((show) =>
      dayjs(show.datetime).isSame(currentDay, "day")
    );

    days.push({
      date: currentDay,
      shows: dayShows,
      isCurrentMonth: currentDay.isSame(month, "month"),
      isToday: currentDay.isSame(today, "day"),
    });

    currentDay = currentDay.add(1, "day");
  }

  return days;
}

const formatMonthName = (month: dayjs.Dayjs) => {
  const now = dayjs();

  // only show year if it's not this year
  if (month.isSame(now, "year")) {
    return month.format("MMMM");
  }

  return month.format("MMMM YYYY");
};

export interface VenueShowsCalendarProps {
  shows: ShowWithComedian[];
  venuePageId: string;
  venueName: string;
  venueSlug?: string;
  venueLogoImg: string | null;
  venueLogoBackgroundColor: string;
  theme?: {
    accentColor: string;
    headerTextColor: string;
    headerBorderColor: string;
    headerBackgroundColor: string;
  };
}

const baseTheme = {
  accentColor: "#ff5e26",
  headerTextColor: "#ffffff",
  headerBorderColor: "#e5e7eb",
  headerBackgroundColor: "#29333C",
};

interface MobileMonthData {
  month: dayjs.Dayjs;
  shows: ShowWithComedian[];
}

// Desktop calendar cells never grow past this many rows. A day with more
// shows renders (DESKTOP_MAX_ROWS - 1) chips plus a "+N more" trigger, so the
// month grid stays compact and uniform no matter how busy a day gets.
const DESKTOP_MAX_ROWS = 4;

/**
 * A single day in the desktop month grid.
 *
 * Every show is individually clickable and opens just that one show — there is
 * no multi-show "buy from a list" surface (which is what led customers to buy
 * the wrong show). When a day has more shows than fit, the overflow collapses
 * into a "+N more" button that opens a compact, day-scoped popover *chooser*:
 * each row is a single show that, when clicked, opens that one show's card. The
 * popover deliberately has no buy buttons — picking and buying never happen on
 * the same multi-show surface.
 */
function DesktopDayCell({
  day,
  accentColor,
  handleShowClick,
  handleShowTitle,
}: {
  day: CalendarDay;
  accentColor: string;
  handleShowClick: (
    show: ShowWithComedian,
    source: "desktop_calendar" | "mobile_agenda"
  ) => void;
  handleShowTitle: (show: ShowWithComedian) => string;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const hasOverflow = day.shows.length > DESKTOP_MAX_ROWS;
  const visibleShows = hasOverflow
    ? day.shows.slice(0, DESKTOP_MAX_ROWS - 1)
    : day.shows;
  const overflowCount = day.shows.length - visibleShows.length;

  const chipClassName =
    "flex flex-row items-center gap-x-1.5 w-full px-2 py-1 text-xs text-left rounded text-primary-blue transition hover:brightness-95 active:brightness-90 cursor-pointer";
  const chipStyle = { backgroundColor: lightenColor(accentColor, 0.8) };

  // Single-line chip: bold start time + truncated title, so several shows fit
  // in a normal-height cell.
  const renderChipContent = (show: ShowWithComedian) => (
    <>
      {show.datetime && (
        <span className="font-semibold shrink-0">
          {dayjs(show.datetime).format("h:mma")}
        </span>
      )}
      <span className="min-w-0 truncate">{handleShowTitle(show)}</span>
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col justify-start items-start min-h-[100px] p-2 border-r border-b border-gray-200 last:border-r-0",
        !day.isCurrentMonth ? "bg-gray-50" : "bg-white",
        day.isToday && "bg-blue-50"
      )}
    >
      <div className="flex flex-row items-start justify-start mb-2">
        <span
          className={cn(
            "text-sm font-semibold",
            !day.isCurrentMonth ? "text-[#A4A6A8]" : "text-primary-blue",
            day.isToday &&
              "bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
          )}
        >
          {day.date.format("D")}
        </span>
      </div>
      <div className="flex flex-col items-start w-full gap-y-1">
        {visibleShows.map((show) => (
          <button
            type="button"
            onClick={() => handleShowClick(show, "desktop_calendar")}
            key={show.id}
            className={chipClassName}
            style={chipStyle}
            title={handleShowTitle(show)}
          >
            {renderChipContent(show)}
          </button>
        ))}
        {overflowCount > 0 && (
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full px-2 py-0.5 text-xs font-medium text-left rounded text-primary-blue hover:underline cursor-pointer"
              >
                +{overflowCount} more
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-2">
              <p className="px-1 pb-2 text-xs font-semibold text-secondary-blue">
                {day.date.format("dddd, MMMM D")}
              </p>
              <div className="flex flex-col gap-y-1 max-h-64 overflow-y-auto">
                {day.shows.map((show) => (
                  <button
                    type="button"
                    key={show.id}
                    onClick={() => {
                      setIsPopoverOpen(false);
                      handleShowClick(show, "desktop_calendar");
                    }}
                    className={chipClassName}
                    style={chipStyle}
                    title={handleShowTitle(show)}
                  >
                    {renderChipContent(show)}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}

function MobileAgendaView({
  allMonths,
  hasMore,
  isLoadingMore,
  loadNextMonth,
  accentColor,
  handleShowClick,
  handleShowTitle,
}: {
  allMonths: MobileMonthData[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadNextMonth: () => void;
  accentColor: string;
  handleShowClick: (
    show: ShowWithComedian,
    source: "desktop_calendar" | "mobile_agenda"
  ) => void;
  handleShowTitle: (show: ShowWithComedian) => string;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const today = dayjs();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadNextMonth();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadNextMonth]);

  return (
    <div className="flex flex-col md:hidden gap-y-6">
      {allMonths.map((monthData) => {
        const monthDaysMap = new Map<string, ShowWithComedian[]>();
        for (const show of monthData.shows) {
          const key = dayjs(show.datetime).format("YYYY-MM-DD");
          const existing = monthDaysMap.get(key) ?? [];
          existing.push(show);
          monthDaysMap.set(key, existing);
        }

        const daysWithShows = Array.from(monthDaysMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([dateKey, shows]) => ({
            date: dayjs(dateKey),
            shows,
            isToday: dayjs(dateKey).isSame(today, "day"),
          }));

        return (
          <div
            key={monthData.month.format("YYYY-MM")}
            className="flex flex-col"
          >
            <h5 className="text-xl font-semibold text-primary-blue pb-3 sticky top-0 bg-white z-10">
              {formatMonthName(monthData.month)}
            </h5>
            <div className="flex flex-col">
              {daysWithShows.map((day) => (
                <div
                  key={day.date.format("YYYY-MM-DD")}
                  className="flex flex-row items-start justify-start w-full py-4 border-b border-gray-200 last:border-b-0"
                >
                  <div className="w-12 text-left shrink-0">
                    <p
                      className={cn(
                        "text-sm leading-none mb-1",
                        day.isToday ? "text-blue-600" : "text-secondary-blue"
                      )}
                    >
                      {day.date.format("ddd")}
                    </p>
                    <p
                      className={cn(
                        "text-2xl font-bold",
                        day.isToday ? "text-blue-600" : "text-primary-blue"
                      )}
                    >
                      {day.date.format("D")}
                    </p>
                  </div>
                  <div className="flex-1 pl-4">
                    <div className="flex flex-col gap-y-3">
                      {day.shows.map((show) => (
                        <button
                          type="button"
                          onClick={() => handleShowClick(show, "mobile_agenda")}
                          key={show.id}
                          className="flex flex-col gap-y-1 w-full text-left text-sm px-2.5 py-3 rounded-lg shadow-sm text-primary-blue transition active:brightness-95"
                          style={{
                            backgroundColor: lightenColor(accentColor, 0.8),
                          }}
                          title={handleShowTitle(show)}
                        >
                          <span className="font-semibold">
                            {handleShowTitle(show)}
                          </span>
                          {show.datetime && (
                            <span>{dayjs(show.datetime).format("h:mma")}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Sentinel for infinite scroll + loading indicator */}
      <div ref={sentinelRef} className="py-4">
        {isLoadingMore && (
          <div className="flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-secondary-blue" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function VenueShowsCalendar({
  shows,
  venuePageId,
  venueName,
  venueSlug,
  venueLogoImg,
  venueLogoBackgroundColor,
  theme = baseTheme,
}: VenueShowsCalendarProps) {
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);
  const [selectedShows, setSelectedShows] = useState<ShowWithComedian[]>([]);

  const {
    currentMonth,
    currentMonthShows,
    goToNextMonth,
    goToPrevMonth,
    canGoNext,
    canGoPrev,
    isLoading,
    allMonths,
    loadNextMonth,
    hasMore,
    isLoadingMore,
  } = useVenueCalendarShows({
    venuePageId,
    initialShows: shows,
  });

  const calendarDays = useMemo(
    () => generateCalendarForMonth(currentMonth, currentMonthShows),
    [currentMonth, currentMonthShows]
  );

  const { accentColor, headerTextColor, headerBackgroundColor } = theme;

  // Opens the modal/drawer for a single show. Clicking any show on the
  // calendar takes the user straight to that one show's card (info +
  // checkout/waitlist). The calendar never shows a multi-show list, so the
  // modal always holds exactly one show — this is what stops customers from
  // buying the wrong show (Creek and the Cave / INB-2).
  const handleShowClick = (
    show: ShowWithComedian,
    source: "desktop_calendar" | "mobile_agenda"
  ) => {
    trackEvent(VenuePageEvents.CALENDAR_SHOW_CLICKED, {
      ShowId: show.id,
      VenueId: show.venue_id,
      Source: source,
    });
    setSelectedShows([show]);
    setIsShowModalOpen(true);
  };

  const formatDateForShows = () => {
    if (selectedShows.length === 0) {
      return "";
    }

    const dates = selectedShows.map((show) => dayjs(show.datetime));
    const earliestDate = dates.reduce((min, d) => (d.isBefore(min) ? d : min));
    const latestDate = dates.reduce((max, d) => (d.isAfter(max) ? d : max));

    const now = dayjs();

    let dateString = "";

    if (earliestDate.isSame(latestDate, "day")) {
      dateString = earliestDate.format("dddd MMMM D");
    } else if (earliestDate.isSame(latestDate, "month")) {
      dateString = `${earliestDate.format("MMMM D")} - ${latestDate.format("D")}`;
    }

    let yearString: string | undefined = undefined;
    if (!earliestDate.isSame(now, "year")) {
      yearString = earliestDate.format(", YYYY");
    }

    return `${dateString}${yearString ?? ""}`;
  };

  const handleShowTitle = (show: ShowWithComedian) => {
    if (show.title) {
      return show.title;
    }

    if (show.show_comedians) {
      return show.show_comedians
        .map((comedian) => comedian.display_name)
        .join(", ");
    }

    return "";
  };

  const weeks: CalendarDay[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const daysWithShows = calendarDays.filter(
    (day) => day.isCurrentMonth && day.shows.length > 0
  );

  const showCount = daysWithShows.reduce(
    (total, day) => total + day.shows.length,
    0
  );

  return (
    <>
      {/* Month Navigation - Desktop only */}
      <div className="hidden md:flex items-center justify-between pb-3">
        <button
          type="button"
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={cn(
            "flex items-center justify-center w-10 h-10 rounded-full transition-colors",
            canGoPrev
              ? "hover:bg-gray-100 active:bg-gray-200 text-primary-blue"
              : "text-gray-300 cursor-not-allowed"
          )}
          aria-label="Previous month"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <h5 className="text-2xl font-semibold text-primary-blue">
            {formatMonthName(currentMonth)}
          </h5>
          {isLoading && (
            <Loader2 className="w-4 h-4 animate-spin text-secondary-blue" />
          )}
        </div>
        <button
          type="button"
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={cn(
            "flex items-center justify-center w-10 h-10 rounded-full transition-colors",
            canGoNext
              ? "hover:bg-gray-100 active:bg-gray-200 text-primary-blue"
              : "text-gray-300 cursor-not-allowed"
          )}
          aria-label="Next month"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Desktop "Calendar" View */}
      <div className="flex-col hidden md:flex gap-y-4">
        <div className="overflow-hidden">
          <div className="flex flex-col border rounded-lg overflow-clip">
            <div
              className="grid grid-cols-7 border-b"
              style={{
                backgroundColor: headerBackgroundColor,
              }}
            >
              {[
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((day: string) => (
                <div
                  key={day}
                  className="p-3 text-sm font-semibold text-center"
                  style={{
                    color: headerTextColor,
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="grid grid-cols-7">
                {week.map((day: CalendarDay) => (
                  <DesktopDayCell
                    key={day.date.format("YYYY-MM-DD")}
                    day={day}
                    accentColor={accentColor}
                    handleShowClick={handleShowClick}
                    handleShowTitle={handleShowTitle}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile "Agenda" View - Infinite scroll through all months */}
      <MobileAgendaView
        allMonths={allMonths}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadNextMonth={loadNextMonth}
        accentColor={accentColor}
        handleShowClick={handleShowClick}
        handleShowTitle={handleShowTitle}
      />

      {/* Show count summary - Desktop only */}
      <div className="hidden md:flex flex-col mt-6 text-sm text-secondary-blue">
        {showCount} {showCount === 1 ? "show" : "shows"} in{" "}
        {formatMonthName(currentMonth)}
      </div>

      <ShowsModalOrDrawer
        title={formatDateForShows()}
        shows={selectedShows}
        isOpen={isShowModalOpen}
        setIsOpen={setIsShowModalOpen}
        venueSlug={venueSlug}
        venueLogoImg={venueLogoImg}
        venueLogoBackgroundColor={venueLogoBackgroundColor}
      />
    </>
  );
}
